/* *
 * ============================================================================
 * Services Data - Centralized Service Definitions
 * ============================================================================
 * All AscendDraft services are defined here. Used by:
 * - Service catalog pages
 * - Reviews system
 * - Pricing system
 * - Order forms
 * 
 * @module data/services
 * @version 1.0.0
 * ============================================================================ */

/* *
 * Service categories
 * @type {Object} */
export const SERVICE_CATEGORIES = Object.freeze({
  ACADEMIC: 'academic',
  CAREER: 'career',
  RESEARCH: 'research',
  PREMIUM: 'premium',
  DESIGN: 'design',
  DIGITAL: 'digital',
  PERSONALIZED: 'personalized'
});

/* *
 * All services offered by AscendDraft
 * @type {Array<Object>} */
export const SERVICES = [
  // ============================================================================
  // PLATFORM-LEVEL SERVICE CATEGORIES (match server seeded services.json keys)
  // These 9 entries drive the Reviews page and service stats.
  // ============================================================================
  {
    id: 'research',
    name: 'Research Services',
    shortName: 'Research',
    category: SERVICE_CATEGORIES.RESEARCH,
    icon: '📜',
    description: 'Research proposals, literature reviews, and data-driven academic research support.',
    shortDescription: 'Research proposals, lit reviews and methodology support',
    path: 'pages/services/academic/#research-proposal',
    priceDisplay: 'From $149',
    priceFrom: 149,
    priceUnit: 'project',
    features: ['Research design', 'Literature analysis', 'Methodology', 'Data support'],
    popular: false,
    reviewsEnabled: true,
    platformLevel: true,
  },
  {
    id: 'premium',
    name: 'Premium Services',
    shortName: 'Premium',
    category: SERVICE_CATEGORIES.PREMIUM,
    icon: '⭐',
    description: 'Executive CVs, career coaching, urgent delivery and top-tier bespoke services.',
    shortDescription: 'Executive CV, career coaching and priority services',
    path: 'services/career/executive/',
    priceDisplay: 'From $199',
    priceFrom: 199,
    priceUnit: 'project',
    features: ['Executive focus', 'Priority queue', 'Dedicated expert', '1-on-1 coaching'],
    popular: false,
    reviewsEnabled: true,
    platformLevel: true,
  },
  {
    id: 'personalized',
    name: 'Personalized Services',
    shortName: 'Personalized',
    category: SERVICE_CATEGORIES.PERSONALIZED,
    icon: '✨',
    description: 'Custom projects, editing, proofreading, and translation tailored to your exact needs.',
    shortDescription: 'Custom projects, editing, proofreading and translation',
    path: 'services/direct-contracts/',
    priceDisplay: 'Custom quote',
    priceFrom: null,
    priceUnit: 'project',
    features: ['Fully custom', 'Any document', 'Native translators', 'Expert editors'],
    popular: false,
    reviewsEnabled: true,
    platformLevel: true,
  },
  {
    id: 'academic',
    name: 'Academic Writing',
    shortName: 'Academic',
    category: SERVICE_CATEGORIES.ACADEMIC,
    icon: '🎯📜',
    description: 'Research papers, dissertations, essays, coursework and more academic writing services.',
    shortDescription: 'Essays, dissertations, coursework and research papers',
    path: 'services/academic/',
    priceDisplay: 'From $15',
    priceFrom: 15,
    priceUnit: 'page',
    features: ['All academic levels', 'Any subject', 'Proper citations', 'Fast delivery'],
    popular: true,
    reviewsEnabled: true,
    platformLevel: true,
  },
  {
    id: 'career',
    name: 'Career Services',
    shortName: 'Career',
    category: SERVICE_CATEGORIES.CAREER,
    icon: '💻',
    description: 'Resume writing, cover letters, LinkedIn optimisation, interview coaching and career strategy.',
    shortDescription: 'CV, cover letter, LinkedIn and career coaching',
    path: 'services/career/',
    priceDisplay: 'From $25',
    priceFrom: 25,
    priceUnit: 'fixed',
    features: ['ATS-optimised', 'Any industry', 'Interview prep', 'LinkedIn review'],
    popular: true,
    reviewsEnabled: true,
    platformLevel: true,
  },
  {
    id: 'design',
    name: 'Design Services',
    shortName: 'Design',
    category: SERVICE_CATEGORIES.DESIGN,
    icon: '🎯',
    description: 'Logos, branding, UI/UX, print and digital design work.',
    shortDescription: 'Logos, branding, UI/UX and digital design',
    path: 'services/design/',
    priceDisplay: 'From $20',
    priceFrom: 20,
    priceUnit: 'fixed',
    features: ['Brand identity', 'Print & digital', 'UI/UX', 'Fast turnaround'],
    popular: false,
    reviewsEnabled: true,
    platformLevel: true,
  },
  {
    id: 'tutoring',
    name: 'Tutoring & Exam Prep',
    shortName: 'Tutoring',
    category: SERVICE_CATEGORIES.ACADEMIC,
    icon: '📚',
    description: 'One-on-one tutoring, exam coaching, subject guidance and study planning.',
    shortDescription: 'One-on-one tutoring and exam coaching',
    path: 'services/tutoring/',
    priceDisplay: 'From $25',
    priceFrom: 25,
    priceUnit: 'hr',
    features: ['Expert tutors', 'All subjects', 'Exam prep', 'Flexible scheduling'],
    popular: false,
    reviewsEnabled: true,
    platformLevel: true,
  },
  {
    id: 'digital',
    name: 'Digital Services',
    shortName: 'Digital',
    category: SERVICE_CATEGORIES.DIGITAL,
    icon: '💻',
    description: 'Web design, development, SEO, digital marketing and online presence services.',
    shortDescription: 'Web design, development, SEO and digital marketing',
    path: 'services/digital/',
    priceDisplay: 'From $30',
    priceFrom: 30,
    priceUnit: 'fixed',
    features: ['Web design', 'Development', 'SEO', 'Digital marketing'],
    popular: false,
    reviewsEnabled: true,
    platformLevel: true,
  },
  {
    id: 'automation',
    name: 'Digital Automation',
    shortName: 'Automation',
    category: SERVICE_CATEGORIES.DIGITAL,
    icon: '™️',
    description: 'Workflow automation, scripts, bots, integration and process optimisation.',
    shortDescription: 'Workflow automation, scripts and process optimisation',
    path: 'services/automation/',
    priceDisplay: 'From $50',
    priceFrom: 50,
    priceUnit: 'fixed',
    features: ['Any platform', 'APIs & integrations', 'Custom scripts', 'Documentation'],
    popular: false,
    reviewsEnabled: true,
    platformLevel: true,
  },
  // ============================================================================
  // ACADEMIC SERVICES (sub-services 📜 ordering/pricing use only)
  // ============================================================================
  {
    id: 'essays',
    name: 'Essays & Coursework',
    shortName: 'Essays',
    category: SERVICE_CATEGORIES.ACADEMIC,
    icon: '📜',
    description: 'Model essays, argumentative papers, and coursework guidance with our ethical 3-tier approach.',
    shortDescription: 'Model essays, research papers, and coursework guidance',
    path: 'pages/services/academic/#essay-writing',
    priceDisplay: '$49 / page',
    priceFrom: 49,
    priceUnit: 'page',
    features: ['All academic levels', 'Any subject', 'Proper citations', 'Plagiarism-free'],
    popular: true,
    reviewsEnabled: true
  },
  {
    id: 'dissertations',
    name: 'Dissertations',
    shortName: 'Dissertations',
    category: SERVICE_CATEGORIES.ACADEMIC,
    icon: '📄',
    description: 'Comprehensive dissertation support from proposal to final submission. Model documents and guidance.',
    shortDescription: 'Comprehensive dissertation support and model documents',
    path: 'pages/services/academic/#dissertation-chapter',
    priceDisplay: '$299 / chapter',
    priceFrom: 299,
    priceUnit: 'chapter',
    features: ['PhD & Masters', 'All disciplines', 'Research methodology', 'Chapter-by-chapter'],
    popular: true,
    reviewsEnabled: true
  },
  {
    id: 'thesis',
    name: 'Thesis Writing',
    shortName: 'Thesis',
    category: SERVICE_CATEGORIES.ACADEMIC,
    icon: '🎯📜',
    description: "Master's thesis support with research methodology guidance and model document creation.",
    shortDescription: 'Master\'s thesis support with research methodology guidance',
    path: 'pages/services/academic/#thesis-writing',
    priceDisplay: '$249 / chapter',
    priceFrom: 249,
    priceUnit: 'chapter',
    features: ['Research design', 'Data analysis', 'Literature review', 'Defense prep'],
    reviewsEnabled: true
  },
  {
    id: 'research-proposal',
    name: 'Research Proposal',
    shortName: 'Research Proposal',
    category: SERVICE_CATEGORIES.RESEARCH,
    icon: '📜',
    description: 'Compelling research proposals with clear objectives, methodology, and literature foundation.',
    shortDescription: 'Research proposals with clear objectives and methodology',
    path: 'pages/services/academic/#research-proposal',
    priceDisplay: '$199 starting',
    priceFrom: 199,
    priceUnit: 'project',
    features: ['Research questions', 'Methodology design', 'Literature foundation', 'Timeline'],
    reviewsEnabled: true
  },
  {
    id: 'literature-review',
    name: 'Literature Review',
    shortName: 'Literature Review',
    category: SERVICE_CATEGORIES.RESEARCH,
    icon: '📖',
    description: 'Comprehensive literature analysis synthesizing key sources and identifying research gaps.',
    shortDescription: 'Literature analysis synthesizing key sources',
    path: 'pages/services/academic/#editing-proofreading',
    priceDisplay: '$149 / section',
    priceFrom: 149,
    priceUnit: 'section',
    features: ['Source synthesis', 'Gap analysis', 'Thematic organization', 'Citation management'],
    reviewsEnabled: true
  },
  {
    id: 'personal-statement',
    name: 'Personal Statement',
    shortName: 'Personal Statement',
    category: SERVICE_CATEGORIES.ACADEMIC,
    icon: '✨',
    description: 'Compelling personal statements for university applications that showcase your unique story.',
    shortDescription: 'Personal statements for university applications',
    path: 'pages/services/academic/#essay-writing',
    priceDisplay: '$199 starting',
    priceFrom: 199,
    priceUnit: 'project',
    features: ['University apps', 'Scholarship apps', 'Unique storytelling', 'Multiple drafts'],
    popular: true,
    reviewsEnabled: true
  },
  {
    id: 'case-study',
    name: 'Case Study Analysis',
    shortName: 'Case Study',
    category: SERVICE_CATEGORIES.ACADEMIC,
    icon: '📜',
    description: 'In-depth case study analysis with theoretical frameworks and practical recommendations.',
    shortDescription: 'Case study analysis with theoretical frameworks',
    path: 'services/academic/case-studies/',
    priceDisplay: '$99 starting',
    priceFrom: 99,
    priceUnit: 'project',
    features: ['Business cases', 'Medical cases', 'Legal cases', 'Framework application'],
    reviewsEnabled: true
  },
  {
    id: 'lab-report',
    name: 'Lab Reports',
    shortName: 'Lab Reports',
    category: SERVICE_CATEGORIES.ACADEMIC,
    icon: '🧪',
    description: 'Scientific lab reports with proper methodology, results analysis, and conclusions.',
    shortDescription: 'Scientific lab reports with methodology and analysis',
    path: 'services/academic/lab-reports/',
    priceDisplay: '$79 starting',
    priceFrom: 79,
    priceUnit: 'project',
    features: ['All sciences', 'Data analysis', 'Graphs & charts', 'Error analysis'],
    reviewsEnabled: true
  },

  // ============================================================================
  // CAREER SERVICES
  // ============================================================================
  {
    id: 'resume',
    name: 'Resume Writing',
    shortName: 'Resume',
    category: SERVICE_CATEGORIES.CAREER,
    icon: '📄',
    description: 'ATS-optimized resumes that get you past the bots and into interviews. Industry-specific formatting.',
    shortDescription: 'ATS-optimized resumes that get you noticed',
    path: 'pages/services/career/#resume',
    priceDisplay: '$299 starting',
    priceFrom: 299,
    priceUnit: 'project',
    features: ['ATS-optimized', 'Industry-specific', 'Keyword optimization', '60-day guarantee'],
    popular: true,
    reviewsEnabled: true
  },
  {
    id: 'cover-letter',
    name: 'Cover Letter',
    shortName: 'Cover Letter',
    category: SERVICE_CATEGORIES.CAREER,
    icon: '📝',
    description: 'Compelling cover letters tailored to specific job postings. Stand out from the competition.',
    shortDescription: 'Tailored cover letters for specific job postings',
    path: 'pages/services/career/#cover-letter',
    priceDisplay: '$199 starting',
    priceFrom: 199,
    priceUnit: 'project',
    features: ['Job-specific', 'Company research', 'Achievement focus', 'Multiple versions'],
    reviewsEnabled: true
  },
  {
    id: 'linkedin',
    name: 'LinkedIn Optimization',
    shortName: 'LinkedIn',
    category: SERVICE_CATEGORIES.CAREER,
    icon: '💻',
    description: 'Complete LinkedIn profile makeover. Keyword optimization for recruiter visibility.',
    shortDescription: 'Complete profile optimization for recruiters',
    path: 'pages/services/career/#linkedin',
    priceDisplay: '$349 starting',
    priceFrom: 349,
    priceUnit: 'project',
    features: ['Headline optimization', 'Summary rewrite', 'Experience highlights', 'Keyword strategy'],
    popular: true,
    reviewsEnabled: true
  },
  {
    id: 'interview-prep',
    name: 'Interview Preparation',
    shortName: 'Interview Prep',
    category: SERVICE_CATEGORIES.CAREER,
    icon: '🎯',
    description: 'Mock interviews, feedback, and coaching. Industry-specific question preparation.',
    shortDescription: 'Mock interviews and coaching sessions',
    path: 'pages/services/career/#interview-prep',
    priceDisplay: '$149 / session',
    priceFrom: 149,
    priceUnit: 'session',
    features: ['Mock interviews', 'Video review', 'STAR method', 'Industry questions'],
    reviewsEnabled: true
  },
  {
    id: 'executive-cv',
    name: 'Executive CV',
    shortName: 'Executive CV',
    category: SERVICE_CATEGORIES.PREMIUM,
    icon: '🔗📜',
    description: 'Premium executive CV writing for C-suite and senior leadership positions.',
    shortDescription: 'Premium CV writing for executives',
    path: 'services/career/executive/',
    priceDisplay: '$599 starting',
    priceFrom: 599,
    priceUnit: 'project',
    features: ['C-suite focus', 'Board experience', 'Achievement metrics', 'Premium design'],
    reviewsEnabled: true
  },
  {
    id: 'career-coaching',
    name: 'Career Coaching',
    shortName: 'Career Coaching',
    category: SERVICE_CATEGORIES.PREMIUM,
    icon: '🎓',
    description: 'One-on-one career coaching to help you navigate your professional journey.',
    shortDescription: 'One-on-one career guidance',
    path: 'services/career/coaching/',
    priceDisplay: '$199 / session',
    priceFrom: 199,
    priceUnit: 'session',
    features: ['1-on-1 sessions', 'Career planning', 'Salary negotiation', 'Transition support'],
    reviewsEnabled: true
  },

  // ============================================================================
  // PERSONALIZED SERVICES
  // ============================================================================
  {
    id: 'custom-project',
    name: 'Custom Project',
    shortName: 'Custom',
    category: SERVICE_CATEGORIES.PERSONALIZED,
    icon: '🎯',
    description: 'Unique projects tailored to your specific needs. Tell us what you need and we\'ll make it happen.',
    shortDescription: 'Tailored projects for your unique needs',
    path: 'services/direct-contracts/',
    priceDisplay: 'Custom quote',
    priceFrom: null,
    priceUnit: 'project',
    features: ['Fully custom', 'Direct collaboration', 'Dedicated expert', 'Flexible timeline'],
    reviewsEnabled: true
  },
  {
    id: 'editing-proofreading',
    name: 'Editing & Proofreading',
    shortName: 'Editing',
    category: SERVICE_CATEGORIES.PERSONALIZED,
    icon: '✏️',
    description: 'Professional editing and proofreading for any document. Grammar, clarity, and style improvements.',
    shortDescription: 'Professional editing for any document',
    path: 'pages/services/academic/#editing-proofreading',
    priceDisplay: '$19 / page',
    priceFrom: 19,
    priceUnit: 'page',
    features: ['Grammar check', 'Style improvement', 'Clarity enhancement', 'Fast turnaround'],
    reviewsEnabled: true
  },
  {
    id: 'translation',
    name: 'Translation Services',
    shortName: 'Translation',
    category: SERVICE_CATEGORIES.PERSONALIZED,
    icon: '',
    description: 'Academic and business document translation by native speakers.',
    shortDescription: 'Document translation by native speakers',
    path: 'services/premium/translation/',
    priceDisplay: '$29 / page',
    priceFrom: 29,
    priceUnit: 'page',
    features: ['50+ languages', 'Native speakers', 'Subject experts', 'Certified available'],
    reviewsEnabled: true
  },
  {
    id: 'urgent-delivery',
    name: 'Urgent Delivery',
    shortName: 'Rush Order',
    category: SERVICE_CATEGORIES.PREMIUM,
    icon: '⚡',
    description: 'Need it fast? Our rush service delivers quality work within 24-48 hours.',
    shortDescription: 'Quality work within 24-48 hours',
    path: 'services/premium/urgent/',
    priceDisplay: '+50% rush fee',
    priceFrom: null,
    priceUnit: 'addon',
    features: ['24-48 hours', 'Priority queue', 'Dedicated writer', 'Quality guaranteed'],
    reviewsEnabled: true
  }
];

/* *
 * Get all services
 * @returns {Array<Object>} */
export function getAllServices() {
  return [...SERVICES];
}

/* *
 * Get services by category
 * @param {string} category - Category to filter by
 * @returns {Array<Object>} */
export function getServicesByCategory(category) {
  if (category === 'all') return [...SERVICES];
  return SERVICES.filter(s => s.category === category);
}

/* *
 * Get a service by ID
 * @param {string} id - Service ID
 * @returns {Object|null} */
export function getServiceById(id) {
  return SERVICES.find(s => s.id === id) || null;
}

/* *
 * Get popular services
 * @returns {Array<Object>} */
export function getPopularServices() {
  return SERVICES.filter(s => s.popular);
}

/* *
 * Get services with reviews enabled
 * @returns {Array<Object>} */
export function getReviewableServices() {
  // Read same platform config cache the nav uses
  let flags = {}, enabled = {};
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem('ascend_nav_config_v1');
    if (raw) {
      const parsed = JSON.parse(raw);
      flags   = parsed?.data?.serviceFlags      || {};
      enabled = parsed?.data?.features?.enabled || {};
    }
  } catch { /* use defaults */ }

  return SERVICES.filter(s => {
    if (!s.platformLevel || !s.reviewsEnabled) return false;
    // Core services (academic, career, design, digital) visible unless explicitly disabled
    // Optional services (tutoring, automation) only shown if explicitly enabled
    const CORE = ['academic', 'career', 'design', 'digital', 'research', 'premium', 'personalized'];
    if (CORE.includes(s.id)) {
      return flags[s.id] !== false && enabled[s.id] !== false;
    }
    // Optional platform services require explicit true
    return flags[s.id] === true;
  });
}

/* *
 * Get service categories for display, filtered by platform config flags.
 * Reads the same localStorage cache the nav uses (ascend_nav_config_v1).
 * Core categories (academic, career, design, digital) show by default unless explicitly disabled.
 * Optional categories (tutoring, automation) only show when explicitly enabled.
 * Unflagged categories (research, premium, personalized) always show.
 * @returns {Array<Object>} */
export function getServiceCategoryList() {
  const ALL_CATEGORIES = [
    { id: SERVICE_CATEGORIES.ACADEMIC,     name: 'Academic',     icon: '📄', flagKey: 'academic',    coreFlag: true  },
    { id: SERVICE_CATEGORIES.CAREER,       name: 'Career',       icon: '💻', flagKey: 'career',      coreFlag: true  },
    { id: SERVICE_CATEGORIES.DESIGN,       name: 'Design',       icon: '🎯', flagKey: 'design',      coreFlag: true  },
    { id: SERVICE_CATEGORIES.DIGITAL,      name: 'Digital',      icon: '💻', flagKey: 'digital',     coreFlag: true  },
    { id: 'tutoring',                      name: 'Tutoring',     icon: '📚', flagKey: 'tutoring',    coreFlag: false },
    { id: 'automation',                    name: 'Automation',   icon: '™️', flagKey: 'automation',  coreFlag: false },
    { id: SERVICE_CATEGORIES.RESEARCH,     name: 'Research',     icon: '📜', flagKey: null },
    { id: SERVICE_CATEGORIES.PREMIUM,      name: 'Premium',      icon: '⭐', flagKey: null },
    { id: SERVICE_CATEGORIES.PERSONALIZED, name: 'Personalized', icon: '✨', flagKey: null },
  ];

  // Read the same platform config cache the navigation component uses
  let flags = {}, enabled = {};
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem('ascend_nav_config_v1');
    if (raw) {
      const parsed = JSON.parse(raw);
      flags   = parsed?.data?.serviceFlags      || {};
      enabled = parsed?.data?.features?.enabled || {};
    }
  } catch { /* use defaults 📜 show core categories */ }

  return ALL_CATEGORIES.filter(cat => {
    if (!cat.flagKey) return true; // research / premium / personalized always shown
    if (cat.coreFlag) {
      // Core categories visible unless explicitly disabled in flags OR features.enabled
      return flags[cat.flagKey] !== false && enabled[cat.flagKey] !== false;
    }
    // Optional categories (tutoring, automation) require explicit true
    return flags[cat.flagKey] === true;
  });
}

// Export for global access
if (typeof window !== 'undefined') {
  window.AscendServices = {
    SERVICES,
    SERVICE_CATEGORIES,
    getAllServices,
    getServicesByCategory,
    getServiceById,
    getPopularServices,
    getReviewableServices,
    getServiceCategoryList
  };
}

export default {
  SERVICES,
  SERVICE_CATEGORIES,
  getAllServices,
  getServicesByCategory,
  getServiceById,
  getPopularServices,
  getReviewableServices,
  getServiceCategoryList
};
